<template>
  <div class="row">
    <div class="col-12 col-md-4 col-lg-3 order-md-2 mb-4">
      <div class="card mb-3">
        <div class="card-body">
          <event-explore-filter
            :formData="formData"
            :useMetric="useMetric"
            :copy="copy"
            :currentFilters="filters"
            @filter-change="handleFilterChange"
          ></event-explore-filter>
        </div>
      </div>

      <div class="card">
        <div class="card-body p-0">
          <race-list
            :races="races"
            :selectedRaceId="selectedRaceId"
            :isLoading="isLoading"
            :useMetric="useMetric"
            :copy="copy"
            :emptyMessage="emptyMessage"
            @race-hover="handleRaceHover"
            @race-click="handleRaceClick"
          ></race-list>
        </div>
      </div>
    </div>

    <div class="col-12 col-md-8 col-lg-9 order-md-1">
      <event-explore-map
        :races="races"
        :mapView="mapView"
        :selectedRaceId="selectedRaceId"
        :hoveredRaceId="hoveredRaceId"
        :useMetric="useMetric"
        :mapboxToken="mapboxToken"
        @map-move="handleMapMove"
        @race-click="handleRaceClick"
        @race-hover="handleRaceHover"
      ></event-explore-map>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import qs from 'qs';

import EventExploreFilter from './event_explore_filter.vue';
import EventExploreMap from './event_explore_map.vue';
import RaceList from './race_list.vue';
import { terrainFor } from '../../utilities/event_filters';

// Format nested params correctly for GET requests
axios.interceptors.request.use(config => {
  config.paramsSerializer = params => {
    return qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: false
    });
  };
  return config;
});

export default {
  props: [
    'initialFilters',
    'initialMapView',
    'formData',
    'useMetric',
    'copy',
    'apiEndpoint',
    'mapboxToken'
  ],

  components: {
    EventExploreFilter,
    EventExploreMap,
    RaceList
  },

  data() {
    return {
      races: [],
      filters: this.initializeFilters(),
      isLoading: false,
      mapBounds: null,
      mapView: this.initialMapView,
      selectedRaceId: null,
      hoveredRaceId: null,
      activeRequestCancelToken: null,
      cancelTokenFactory: axios.CancelToken,
      emptyMessage: null
    };
  },

  mounted() {
    // Don't fetch on mount - wait for map to load and emit initial bounds
  },

  methods: {
    initializeFilters() {
      const initial = this.initialFilters || {};
      const activityType = initial.activityType ||
        this.formData.activityTypes[0]?.value || null;
      const filters = {
        country: initial.country,
        region: initial.region,
        activity_type: activityType,
        terrain: terrainFor(activityType, initial.terrain),
        date_range: {
          start: initial.startDate,
          end: initial.endDate
        }
      };

      // Only include distance if it has proper values from the server (in meters)
      // Otherwise, let the filter component use its own defaults
      if (initial.distance && initial.distance.min >= 1000) {
        filters.distance = initial.distance;
      }

      return filters;
    },

    handleFilterChange(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.debouncedFetchRaces();
    },

    handleMapMove(bounds) {
      this.mapBounds = bounds;
      this.debouncedFetchRaces();
    },

    handleRaceHover(raceId) {
      this.hoveredRaceId = raceId;
    },

    handleRaceClick(raceId) {
      const race = this.races.find(r => r.id === raceId);
      if (race && race.path) {
        window.location.href = race.path;
      }
    },

    debouncedFetchRaces: _.debounce(function() {
      this.fetchRaces();
    }, 500),

    fetchRaces() {
      const self = this;

      // Don't fetch if we don't have map bounds yet
      if (!this.mapBounds) {
        return;
      }

      self.isLoading = true;

      // Cancel any currently pending request
      if (self.activeRequestCancelToken) {
        self.activeRequestCancelToken.cancel('Subsequent request made');
      }

      // Generate a cancel token for this request
      self.activeRequestCancelToken = self.cancelTokenFactory.source();

      const params = {
        filters: {
          activity_type: this.filters.activity_type,
          terrain: terrainFor(this.filters.activity_type, this.filters.terrain),
          distance: this.filters.distance,
          date_range: this.filters.date_range,
          map_bounds: this.mapBounds
          // Note: Don't send country/region when using map_bounds (API requires one or the other)
        }
      };

      axios.get(self.apiEndpoint, {
        headers: { 'Accept': 'application/json' },
        params: params,
        cancelToken: self.activeRequestCancelToken.token
      }).then(response => {
        self.races = response.data.races || [];
        self.emptyMessage = response.data.message || null;
        self.activeRequestCancelToken = null;
        self.isLoading = false;
      }).catch(function(thrown) {
        if (axios.isCancel(thrown)) {
          console.log(`Request canceled: ${thrown.message}`);
        } else {
          console.error('Error fetching races:', thrown.message);
          self.isLoading = false;
        }
      });
    }
  }
};
</script>
